import React, { useState, useRef, useEffect } from 'react';

export const Select = ({ children, onValueChange, defaultValue, value, disabled, displayLabel, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue || '');
  const [selectedLabel, setSelectedLabel] = useState('');
  const selectRef = useRef(null);

  // Update selectedValue when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleValueChange = (newValue, label) => {
    setSelectedValue(newValue);
    setSelectedLabel(label);
    setIsOpen(false);
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  const handleTriggerClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div ref={selectRef} className="relative" {...props}>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          if (child.type === SelectTrigger) {
            return React.cloneElement(child, {
              onClick: handleTriggerClick,
              disabled,
              isOpen,
              selectedValue,
              selectedLabel,
              displayLabel
            });
          }
          if (child.type === SelectContent) {
            return React.cloneElement(child, {
              isOpen,
              onValueChange: handleValueChange,
              selectedValue
            });
          }
        }
        return child;
      })}
    </div>
  );
};

export const SelectTrigger = React.forwardRef(({ 
  className = '', 
  children, 
  onClick, 
  disabled, 
  isOpen, 
  selectedValue,
  selectedLabel,
  displayLabel,
  ...props 
}, ref) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
  };

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    >
      <span className="flex-1 text-left truncate">
        {React.Children.map(children, child => {
          if (React.isValidElement(child) && child.type === SelectValue) {
            return React.cloneElement(child, {
              selectedValue,
              selectedLabel,
              displayLabel
            });
          }
          return child;
        })}
      </span>
      <svg
        className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
});

export const SelectContent = ({ 
  children, 
  className = '', 
  isOpen, 
  onValueChange, 
  selectedValue,
  ...props 
}) => {
  if (!isOpen) return null;

  return (
    <div
      className={`absolute z-50 w-full mt-1 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg ${className}`}
      {...props}
    >
      {React.Children.map(children, child => {
        if (React.isValidElement(child) && child.type === SelectItem) {
          return React.cloneElement(child, {
            onValueChange,
            selectedValue,
            isSelected: child.props.value === selectedValue
          });
        }
        return child;
      })}
    </div>
  );
};

export const SelectItem = React.forwardRef(({ 
  className = '', 
  children, 
  value, 
  onValueChange, 
  selectedValue,
  isSelected,
  ...props 
}, ref) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onValueChange && value !== undefined) {
      onValueChange(value, children);
    }
  };

  return (
    <div
      ref={ref}
      onClick={handleClick}
      className={`relative flex w-full cursor-pointer select-none items-center py-2 px-3 text-sm hover:bg-gray-100 ${
        isSelected ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
      } ${className}`}
      {...props}
    >
      {isSelected && (
        <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
      {children}
    </div>
  );
});

export const SelectValue = ({ placeholder = '', className = '', selectedValue, selectedLabel, displayLabel, ...props }) => {
  // Öncelik: kullanıcı seçiminden gelen label, sonra dışarıdan verilen displayLabel (örn. form yüklendiğinde), sonra value, placeholder
  const displayValue = selectedLabel || displayLabel || (selectedValue ? selectedValue : placeholder);
  
  return (
    <span className={`text-left ${!selectedValue ? 'text-gray-500' : 'text-gray-900'} ${className}`} {...props}>
      {displayValue}
    </span>
  );
};

SelectTrigger.displayName = 'SelectTrigger';
SelectItem.displayName = 'SelectItem'; 